import { useEffect, useRef } from "react";
import {
  appMenuStore,
  useAppMenuBar,
  useAppMenuOpen,
  type AppMenuItem,
} from "../os/app-menu";

export function AppWindowMenuBar({ windowId }: { windowId: string }) {
  const menus = useAppMenuBar(windowId);
  const open = useAppMenuOpen();
  const rootRef = useRef<HTMLDivElement>(null);

  // Only the window with an open menu installs the capture outside-click listener.
  // With 2+ apps open, every WindowFrame used to install one; sibling App B then
  // treated App A's menu click as outside, closed the menu on mousedown, and
  // File -> Open never received the click.
  useEffect(() => {
    const isActive = open.windowId === windowId && !!open.menuId;
    if (!isActive) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && rootRef.current?.contains(target)) return;
      if (target instanceof Element) {
        if (target.closest(".app-menubar, .os-dialog-backdrop, .ctx-menu, .update-panel")) {
          return;
        }
      }
      appMenuStore.closeOpen();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") appMenuStore.closeOpen();
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open.windowId, open.menuId, windowId]);

  if (!menus.length) return null;

  const active =
    open.windowId === windowId ? open.menuId : null;

  return (
    <div
      className="app-menubar"
      ref={rootRef}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {menus.map((menu) => {
        const isOpen = active === menu.id;
        return (
          <div key={menu.id} className={`app-menu-root ${isOpen ? "open" : ""}`}>
            <button
              type="button"
              className={`app-menu-btn ${isOpen ? "open" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (isOpen) appMenuStore.closeOpen();
                else appMenuStore.setOpen(windowId, menu.id);
              }}
              onMouseEnter={() => {
                // Windows-style: once a menu is open, hover switches tops
                if (open.menuId && open.windowId === windowId) {
                  appMenuStore.setOpen(windowId, menu.id);
                }
              }}
            >
              {menu.label}
            </button>
            {isOpen && (
              <div className="app-menu-dropdown glass glass-strong" role="menu">
                {menu.items.map((item) =>
                  item.separator ? (
                    <div key={item.id} className="app-menu-sep" />
                  ) : (
                    <MenuRow
                      key={item.id}
                      item={item}
                      onDone={() => appMenuStore.closeOpen()}
                    />
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MenuRow({
  item,
  onDone,
}: {
  item: AppMenuItem;
  onDone: () => void;
}) {
  const disabled = !!item.disabled || !item.action;
  const run = () => {
    if (disabled) return;
    // Close the menu first, then run the action on the next task.
    // Opening OS dialogs synchronously on mousedown mounts a full-screen
    // backdrop under the still-held cursor; the leftover mouseup/click then
    // hits the backdrop and instantly dismisses the Open/Save dialog.
    onDone();
    window.setTimeout(() => {
      try {
        item.action?.();
      } catch {
        /* */
      }
    }, 10);
  };
  return (
    <button
      type="button"
      role="menuitem"
      className={`app-menu-item ${item.danger ? "danger" : ""}`}
      disabled={disabled}
      onClick={(e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        run();
      }}
    >
      <span className="app-menu-label">{item.label}</span>
      {item.shortcut ? (
        <span className="app-menu-shortcut">{item.shortcut}</span>
      ) : null}
    </button>
  );
}
